import React, { useState, useEffect, useCallback } from "react";
import { FileText, Eye, Send, Edit, X } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Cd, Bt, Sl } from "./index";
import { RichTextEditor } from "./RichTextEditor";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

// Employee → Settings → "Send documents": offer letter, NDA and employment contract for a current employee.
// HR picks which to send, previews each as the PDF the employee will receive (salary structure filled in from the
// employee's saved structure), can edit the text for this one send, then sends. The saved templates are changed in
// Recruitment → Offer Templates. They also go out automatically three months after joining.
const DOCS = [
  { key: "offer_letter", label: "Offer letter" },
  { key: "nda", label: "NDA" },
  { key: "contract", label: "Employment contract" },
];
const FIELDS = {
  offer_letter: ["candidate_name", "candidate_address_block", "designation", "company_name", "location", "ctc", "ctc_words", "join_date", "offer_date", "stamp_image", "document_checklist"],
  nda: ["employee_name", "employee_id", "designation", "department", "company_name", "place_of_work", "agreement_date", "join_date", "signatory_name", "signatory_designation", "stamp_image"],
  contract: ["employee_name", "employee_id", "designation", "department", "company_name", "place_of_work", "agreement_date", "join_date", "working_days", "shift_start", "shift_end", "ctc_amount", "ctc_words", "salary_table", "signatory_name", "signatory_designation", "stamp_image"],
};

export const EmployeeDocsSend = ({ empId, name }) => {
  const [tpls, setTpls] = useState(null);
  const [history, setHistory] = useState([]);
  const [sel, setSel] = useState({}); // key -> { on, templateId, body (edited copy, or null), editing }
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/api/employee-docs/templates").then(d => {
      setTpls(d || {});
      setSel(prev => {
        const next = { ...prev };
        DOCS.forEach(({ key }) => {
          const list = d?.[key]?.templates || [];
          if (!next[key]) next[key] = { on: false, templateId: (list.find(t => t.is_default) || list[0])?.id || null, body: null, editing: false };
        });
        return next;
      });
    }).catch(() => setTpls({}));
    api.get("/api/employee-docs/history/" + empId).then(d => setHistory(d?.data || [])).catch(() => setHistory([]));
  }, [empId]);
  useEffect(() => { setSel({}); setTpls(null); load(); }, [empId, load]);

  const patch = (key, p) => setSel(s => ({ ...s, [key]: { ...s[key], ...p } }));
  const tplOf = (key) => (tpls?.[key]?.templates || []).find(t => t.id === sel[key]?.templateId) || null;

  const payload = (key) => ({ doc_type: key, template_id: sel[key].templateId, body_html: sel[key].body || undefined });

  const preview = async (key) => {
    setBusy("preview-" + key);
    try {
      const res = await fetch(`${BASE}/api/employee-docs/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${api.getToken()}` },
        body: JSON.stringify({ employee_id: empId, ...payload(key) }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || "Preview failed"); }
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { notify(e.message || "Preview failed", { title: "Error", variant: "error" }); }
    finally { setBusy(null); }
  };

  const chosen = DOCS.filter(d => sel[d.key]?.on);
  const send = async () => {
    if (!chosen.length) return notify("Tick at least one document to send.", { title: "Check", variant: "error" });
    const names = chosen.map(d => d.label).join(", ");
    if (!(await confirmDialog(`Send ${names} to ${name} by email?\n\nThey receive the PDF${chosen.length > 1 ? "s" : ""} exactly as in the preview.`))) return;
    setBusy("send");
    try {
      const r = await api.post("/api/employee-docs/send", { employee_id: empId, docs: chosen.map(d => payload(d.key)) });
      notify(`${names} sent to ${r?.to || name}`, { title: "Sent", variant: "success" });
      setSel(s => { const n = { ...s }; DOCS.forEach(({ key }) => { n[key] = { ...n[key], on: false, body: null, editing: false }; }); return n; });
      load();
    } catch (e) { notify(e.message || "Could not send", { title: "Error", variant: "error" }); }
    finally { setBusy(null); }
  };

  const lastSent = (key) => history.find(h => h.doc_type === key);

  return (
    <Cd style={{ marginTop: 12, borderLeft: `3px solid ${K.p}` }}>
      <div className="flex items-center gap-2 mb-1"><FileText size={15} color={K.p} /><span className="text-[15px] font-extrabold text-t0">Send documents to {name}</span></div>
      <div className="text-[13px] text-t2 mb-3">
        Offer letter, NDA and employment contract on the company letterhead, with the salary taken from this employee's saved salary structure. Preview each one, change the wording for this send if needed, then send.
        They are also sent automatically three months after joining. To change a template for everyone, use Recruitment → Offer Templates.
      </div>
      {tpls === null && <div className="text-[13px] text-t3">Loading…</div>}
      {tpls && DOCS.map(({ key, label }) => {
        const s = sel[key]; if (!s) return null;
        const list = tpls[key]?.templates || [];
        const last = lastSent(key);
        return (
          <div key={key} className="border border-b0 rounded-[10px] p-3 mb-2 bg-bg1">
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer min-w-[190px]">
                <input type="checkbox" checked={!!s.on} onChange={e => patch(key, { on: e.target.checked })} />
                <span className="text-[14px] font-bold text-t0">{label}</span>
              </label>
              {list.length > 1 && <div className="w-[220px]"><Sl options={list.map(t => ({ v: String(t.id), l: t.name + (t.is_default ? " (default)" : "") }))} value={String(s.templateId || "")} onChange={v => patch(key, { templateId: parseInt(v, 10), body: null, editing: false })} /></div>}
              {list.length === 1 && <span className="text-[12px] text-t3">{list[0].name}</span>}
              {list.length === 0 && <span className="text-[12px] text-r">No template yet — add one in Recruitment → Offer Templates.</span>}
              <div className="flex-1" />
              {last && <span className="text-[12px] text-t3">Last sent {fmtDMY(last.sent_at)} ({last.mode === "auto" ? "automatic" : "by HR"})</span>}
              <Bt v="secondary" s="sm" icon={Eye} disabled={!list.length || busy === "preview-" + key} onClick={() => preview(key)}>{busy === "preview-" + key ? "Opening…" : "Preview"}</Bt>
              <Bt v="ghost" s="sm" icon={s.editing ? X : Edit} disabled={!list.length} onClick={() => s.editing ? patch(key, { editing: false, body: null }) : patch(key, { editing: true, body: (tplOf(key)?.body_html || "") })}>{s.editing ? "Discard edits" : "Edit text"}</Bt>
            </div>
            {s.editing && (
              <div className="mt-3">
                <div className="text-[12px] text-t3 mb-2">This copy is used only for this send. Fields like {"{{employee_name}}"} are filled in automatically.</div>
                <RichTextEditor key={key + ":" + s.templateId} value={s.body || ""} onChange={v => patch(key, { body: v })} mergeFields={FIELDS[key]} fieldFormat={(f) => `{{${f}}}`} minHeight={320} />
              </div>
            )}
          </div>
        );
      })}
      {tpls && <div className="mt-3"><Bt icon={Send} disabled={busy === "send" || !chosen.length} onClick={send}>{busy === "send" ? "Sending…" : chosen.length ? `Send ${chosen.length} document${chosen.length > 1 ? "s" : ""}` : "Send"}</Bt></div>}
    </Cd>
  );
};
